import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Avatar, Tag, Rate, Spin, Empty, Space, Tabs } from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { productService } from '../services/api';

const { Title, Paragraph, Text } = Typography;

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price || 0);
};

const ProviderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes] = await Promise.all([
          productService.getProducts({ provider_id: id, limit: 20 }),
          productService.getProductDetail(id).catch(() => null),
        ]);

        const productsData = productsRes?.data?.data || productsRes?.data || [];
        setProducts(Array.isArray(productsData) ? productsData : []);

        // Get provider info from products
        if (productsData.length > 0) {
          const firstProduct = productsData[0];
          setProvider({
            id: parseInt(id),
            full_name: firstProduct.provider?.name || firstProduct.provider_name || 'Provider',
            business_name: firstProduct.provider?.business_name || '',
            email: firstProduct.provider?.email || '',
            phone: firstProduct.provider?.phone || '',
            address: firstProduct.provider?.address || '',
            profile_image: firstProduct.provider?.profile_image || null,
            average_rating: firstProduct.provider?.average_rating || 4.5,
            total_products: productsData.length,
            total_reviews: firstProduct.provider?.total_reviews || 0,
            description:
              firstProduct.provider?.description ||
              'Professional service provider offering quality products and services.',
          });
        }
      } catch (error) {
        console.error('Error fetching provider:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div style={{ padding: '24px' }}>
        <Empty description="Provider not found" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px' }}>
      <Card style={{ borderRadius: 16, marginBottom: 24 }}>
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={6} style={{ textAlign: 'center' }}>
            <Avatar
              size={120}
              src={provider.profile_image}
              icon={<UserOutlined />}
              style={{ border: '4px solid #e8f0e8' }}
            />
          </Col>
          <Col xs={24} md={18}>
            <Title level={2} style={{ margin: 0, color: '#2d5a3d' }}>
              {provider.full_name}
            </Title>
            {provider.business_name && (
              <Text style={{ color: '#6b7280', fontSize: 16 }}>{provider.business_name}</Text>
            )}
            <div style={{ marginTop: 12 }}>
              <Rate disabled defaultValue={provider.average_rating} allowHalf />
              <Text style={{ marginLeft: 8, color: '#6b7280' }}>
                ({provider.total_reviews} reviews)
              </Text>
            </div>
            <Paragraph style={{ marginTop: 12, color: '#4b5563' }}>
              {provider.description}
            </Paragraph>
            <Space wrap>
              {provider.phone && (
                <Tag icon={<PhoneOutlined />} color="blue">
                  {provider.phone}
                </Tag>
              )}
              {provider.email && <Tag icon={<MailOutlined />}>{provider.email}</Tag>}
              {provider.address && <Tag icon={<EnvironmentOutlined />}>{provider.address}</Tag>}
              <Tag icon={<SafetyCertificateOutlined />} color="green">
                Verified
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      <Tabs
        defaultActiveKey="products"
        items={[
          {
            key: 'products',
            label: `Products & Services (${products.length})`,
            children:
              products.length === 0 ? (
                <Empty description="No products listed yet" />
              ) : (
                <Row gutter={[16, 16]}>
                  {products.map((product) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
                      <Card
                        hoverable
                        cover={
                          <img
                            alt={product.name}
                            src={
                              product.images?.[0]?.url ||
                              `https://placehold.co/300x200?text=${encodeURIComponent(product.name)}`
                            }
                            style={{ height: 160, objectFit: 'cover' }}
                          />
                        }
                        onClick={() => navigate(`/products/${product.id}`)}
                        style={{ borderRadius: 12 }}
                      >
                        <Tag color={product.type === 'service' ? 'blue' : 'green'}>
                          {product.type || 'Product'}
                        </Tag>
                        <Title level={5} style={{ margin: '8px 0 4px', color: '#2d5a3d' }}>
                          {product.name}
                        </Title>
                        <Text strong style={{ color: '#52b788', fontSize: 16 }}>
                          {formatPrice(product.sale_price ?? product.price)}
                        </Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ),
          },
          {
            key: 'reviews',
            label: 'Reviews',
            children: <Empty description="No reviews yet" />,
          },
        ]}
      />
    </div>
  );
};

export default ProviderProfile;
